import { useState } from "react";
import { Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { inputCls, primaryBtn } from "@/components/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const SKIP_KEY = "ct_phone_prompt_skipped";
const valid = (p) => /^\+?[0-9 ]{7,18}$/.test(p.trim());

const PhoneForm = ({ onSaved }) => {
  const { refresh } = useAuth();
  const [phone, setPhone] = useState("+675 ");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!valid(phone)) return setError("Enter a valid mobile number, e.g. +675 7123 4567.");
    setBusy(true);
    setError("");
    try {
      await api.put("/auth/me/phone", { phone: phone.trim() });
      await refresh();
      toast.success("Mobile number saved. You'll get SMS ride alerts.");
      onSaved?.();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="flex flex-col gap-3 sm:flex-row" data-testid="phone-prompt-form">
      <input data-testid="phone-prompt-input" type="tel" inputMode="tel" required className={`${inputCls} sm:flex-1`} value={phone} onChange={(e) => setPhone(e.target.value)} />
      <button data-testid="phone-prompt-save" disabled={busy} className={`${primaryBtn} sm:w-40`}>{busy ? "Saving…" : "Save number"}</button>
      {error && <p data-testid="phone-prompt-error" className="text-sm text-red-600 sm:basis-full">{error}</p>}
    </form>
  );
};

export const PhonePrompt = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(() => !sessionStorage.getItem(SKIP_KEY));
  if (user?.role !== "customer" || user.phone) return null;
  const skip = () => {
    sessionStorage.setItem(SKIP_KEY, "1");
    setOpen(false);
  };
  return (
    <>
      <div data-testid="phone-prompt-banner" className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4">
        <p className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Smartphone className="h-4 w-4 text-amber-600" /> Add your mobile number to get SMS ride alerts</p>
        <p className="mb-3 mt-1 text-sm text-slate-600">We'll text you when your driver accepts, arrives and finishes your trip.</p>
        <PhoneForm />
      </div>
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : skip())}>
        <DialogContent data-testid="phone-prompt-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>What's your mobile number?</DialogTitle>
            <DialogDescription>Comfort Taxi texts you when your driver accepts, arrives and finishes your ride. Drivers may also call you at pickup.</DialogDescription>
          </DialogHeader>
          <PhoneForm onSaved={() => setOpen(false)} />
          <button data-testid="phone-prompt-skip" onClick={skip} className="text-sm font-semibold text-slate-500 hover:text-slate-700">Not now</button>
        </DialogContent>
      </Dialog>
    </>
  );
};
